import React, { useEffect, useRef, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * InteractiveArticleViewer - Renders complete HTML articles in an isolated iframe
 * 
 * This component is designed to display pre-built HTML articles with their own
 * CSS and JavaScript without style conflicts with the main site.
 * 
 * Features:
 * - Auto-resizing iframe to fit content height
 * - No nested scrolling - iframe expands to full content
 * - Isolated styles (iframe sandbox)
 * - Bridge injection for poll integration
 * - Supports local files from /public/articles/ or external URLs
 */
const InteractiveArticleViewer = ({ articleUrl, title = 'Article' }) => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(2000);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, login } = useContext(AuthContext);

  // Inject the bridge into the iframe after it loads
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const injectBridge = () => {
      try {
        const iframeWindow = iframe.contentWindow;
        if (!iframeWindow) return;

        // Create bridge functions that communicate with parent
        const bridgeCode = `
          (function() {
            // Create the bridge object for polls
            const API = '${API}';
            
            const bridge = {
              getPoll: function(pollId) {
                return fetch(API + '/polls/game/' + pollId, {
                  credentials: 'include'
                })
                .then(function(r) { return r.json(); })
                .then(function(data) {
                  return {
                    authenticated: data.authenticated,
                    voted: data.voted,
                    counts: data.counts,
                    selectedIndex: data.selected_index,
                    myVote: data.selected_index,
                    totalVotes: data.total_votes,
                    options: data.options,
                    question: data.question
                  };
                })
                .catch(function(e) {
                  console.error('getPoll error:', e);
                  return { authenticated: false, voted: false, counts: [], error: e.message };
                });
              },
              
              vote: function(pollId, optionIndex) {
                return fetch(API + '/polls/game/' + pollId + '/vote', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ option_index: optionIndex })
                })
                .then(function(r) {
                  if (r.status === 401) {
                    return { success: false, requiresAuth: true, error: 'Please log in to vote' };
                  }
                  return r.json();
                })
                .then(function(data) {
                  if (data.requiresAuth) return data;
                  return {
                    success: true,
                    authenticated: true,
                    voted: true,
                    counts: data.counts,
                    selectedIndex: data.selected_index,
                    myVote: data.selected_index,
                    totalVotes: data.total_votes
                  };
                })
                .catch(function(e) {
                  console.error('vote error:', e);
                  return { success: false, error: e.message };
                });
              },
              
              login: function() {
                // Store return URL and notify parent to trigger login
                window.parent.postMessage({ type: 'slt-bridge-login', returnUrl: window.location.href }, '*');
              },
              
              isAuthenticated: function() {
                return ${user ? 'true' : 'false'};
              },
              
              getUser: function() {
                return ${user ? JSON.stringify({ name: user.name, picture: user.picture }) : 'null'};
              }
            };
            
            // Also create get/vote aliases for SheLovesTheePolls compatibility
            const pollsBridge = {
              get: function(pollId) {
                return bridge.getPoll(pollId).then(function(r) {
                  r.myVote = r.selectedIndex;
                  return r;
                });
              },
              vote: bridge.vote,
              isLoggedIn: bridge.isAuthenticated,
              login: bridge.login,
              getUser: bridge.getUser
            };
            
            // Expose both bridges
            window.SLTGameBridge = bridge;
            window.SheLovesTheePolls = pollsBridge;
            
            // Dispatch ready events
            console.log('[Bridge] Injected into iframe, dispatching ready events');
            window.dispatchEvent(new Event('slt-game-bridge-ready'));
            window.dispatchEvent(new Event('shelovesthee-polls-ready'));
            
            // Also dispatch after delays for late-loading scripts
            setTimeout(function() {
              window.dispatchEvent(new Event('slt-game-bridge-ready'));
              window.dispatchEvent(new Event('shelovesthee-polls-ready'));
            }, 500);
            setTimeout(function() {
              window.dispatchEvent(new Event('slt-game-bridge-ready'));
              window.dispatchEvent(new Event('shelovesthee-polls-ready'));
            }, 1500);
          })();
        `;

        // Inject the script into the iframe
        const script = iframeWindow.document.createElement('script');
        script.textContent = bridgeCode;
        iframeWindow.document.head.appendChild(script);
        
        console.log('[InteractiveArticleViewer] Bridge injected into iframe');
      } catch (e) {
        console.error('Failed to inject bridge:', e);
      }
    };

    const handleLoad = () => {
      setIsLoading(false);
      
      // Inject bridge after a short delay to ensure DOM is ready
      setTimeout(injectBridge, 100);
      
      // Try to get the content height and resize iframe
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Wait a bit for content to fully render
          setTimeout(() => {
            const height = iframeDoc.documentElement.scrollHeight || iframeDoc.body.scrollHeight;
            if (height > 100) {
              setIframeHeight(height + 50); // Add padding
            }
          }, 500);

          // Also set up a resize observer for dynamic content
          setTimeout(() => {
            const height = iframeDoc.documentElement.scrollHeight || iframeDoc.body.scrollHeight;
            if (height > 100) {
              setIframeHeight(height + 50);
            }
          }, 2000);
        }
      } catch (e) {
        // Cross-origin restrictions may prevent height detection
        console.log('Could not detect iframe height:', e.message);
        setIframeHeight(5000); // Default to large height for external content
      }
    };

    const handleError = () => {
      setIsLoading(false);
      setError('Failed to load article content');
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
    };
  }, [articleUrl, user]);

  // Listen for login requests from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'slt-bridge-login') {
        // Store return URL and trigger login
        // CRITICAL: Use localStorage, NOT sessionStorage - sessionStorage is wiped during cross-domain auth redirect
        const returnUrl = window.location.pathname + window.location.search;
        localStorage.setItem('authReturnUrl', returnUrl);
        login();
      }
      if (event.data && event.data.type === 'articleHeight') {
        setIframeHeight(event.data.height + 50);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [login]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-600 font-semibold">{error}</p>
        <p className="text-gray-600 mt-2">Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="interactive-article-container w-full">
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Loading article...</span>
        </div>
      )}
      
      <iframe
        ref={iframeRef}
        src={articleUrl}
        title={title}
        className="w-full border-0"
        style={{
          height: `${iframeHeight}px`,
          display: isLoading ? 'none' : 'block',
          overflow: 'hidden'
        }}
        scrolling="no"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    </div>
  );
};

export default InteractiveArticleViewer;
